import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ roadmap, overallProgress }) {
  const links = (roadmap.links || []).filter((l) => l.url);
  const steam = links.find((l) => l.label.toUpperCase().includes("STEAM"));
  const discord = links.find((l) => l.label.toUpperCase().includes("DISCORD"));

  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 180]);

  return (
    <header data-testid="hero-section" className="relative border-b border-white/10 overflow-hidden">
      <motion.div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          y: bgY,
          backgroundImage:
            "url('https://images.unsplash.com/photo-1605256801685-06e9af2a74f2?auto=format&fit=crop&w=1920&q=60')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-[#050505]/85 to-[#050505]" />
      <div className="absolute inset-0 grid-texture opacity-60" />

      <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-14 md:pt-28 md:pb-20">
        <motion.p
          data-testid="hero-studio"
          className="font-mono text-xs tracking-[0.4em] text-zinc-500 uppercase mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.15 }}
        >
          {roadmap.studio} Presents
        </motion.p>

        <motion.div
          initial={{ clipPath: "inset(0 100% 0 0)" }}
          animate={{ clipPath: "inset(0 0% 0 0)" }}
          transition={{ duration: 1.2, delay: 0.35, ease: EASE }}
        >
          <img
            data-testid="hero-game-title"
            src="/assets/newcitizen-wordmark.png"
            alt={roadmap.game}
            className="w-full max-w-2xl h-auto"
          />
        </motion.div>

        <MaskedLine delay={0.7} className="mt-4">
          <span data-testid="hero-game-tagline" className="font-mono text-xs sm:text-sm tracking-[0.35em] text-nc-orange uppercase">
            {roadmap.gameTagline}
          </span>
        </MaskedLine>

        <motion.div
          className="mt-8 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
        >
          {steam && (
            <a
              data-testid="hero-wishlist-button"
              href={steam.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-sm px-7 py-3.5 transition-colors duration-300"
              style={{ boxShadow: "0 0 24px rgba(255, 107, 26, 0.25)" }}
            >
              Play on Steam
            </a>
          )}
          {discord && (
            <a
              data-testid="hero-discord-button"
              href={discord.url}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-nc-blue/60 text-nc-blue hover:bg-nc-blue/10 font-sans font-semibold uppercase tracking-[0.2em] text-sm px-7 py-3.5 transition-colors duration-300"
            >
              Join the Discord
            </a>
          )}
        </motion.div>

        <div className="mt-12 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-10">
          <div>
            <MaskedLine delay={1.05}>
              <span data-testid="hero-roadmap-title" className="font-sans font-semibold uppercase tracking-wide text-2xl md:text-3xl text-zinc-100">
                <span className="text-nc-blue">{roadmap.version}</span> {roadmap.title}
              </span>
            </MaskedLine>
            <motion.p
              data-testid="hero-motto"
              className="font-mono text-[11px] sm:text-xs tracking-[0.25em] text-zinc-500 uppercase mt-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1.25 }}
            >
              {roadmap.motto}
            </motion.p>
          </div>

          <motion.div
            className="sm:ml-auto w-full sm:w-72"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.35, ease: EASE }}
          >
            <div className="flex justify-between items-baseline mb-2">
              <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase">Overall Progress</span>
              <span data-testid="hero-overall-progress" className="font-mono text-sm text-nc-green">{overallProgress}%</span>
            </div>
            <div className="h-1.5 w-full bg-white/10 relative overflow-hidden">
              <motion.div
                className="absolute inset-y-0 left-0 bg-nc-green"
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 1.5, ease: "easeOut", delay: 1.6 }}
                style={{ boxShadow: "0 0 20px rgba(0, 230, 118, 0.35)" }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </header>
  );
}
